import i18n from 'components/translation/i18n';
/**
 * @function appLocale
 * @since 1.0.0
 * Number/date locale matching the interface language.
 */
export function appLocale(language = i18n.language) {
    const code = (language || 'en').substring(0, 2);
    if (code === 'fr')
        return 'fr-CA';
    if (code === 'es')
        return 'es';
    return 'en-CA';
}
/**
 * Currency used when none is given. CAD shows a plain "$" in both en-CA and
 * fr-CA, as the app always did; other currencies are told apart ("$ US", "€").
 */
export const DEFAULT_CURRENCY = 'CAD';
/**
 * @function formatMoney
 * @since 1.0.0
 * Amount in the interface language, in the given ISO currency.
 */
export function formatMoney(value, options = {}) {
    const { decimals = 2, compact = false } = options;
    const currency = (options.currency || DEFAULT_CURRENCY).toUpperCase();
    const amount = Number(value) || 0;
    try {
        return new Intl.NumberFormat(appLocale(), {
            style: 'currency',
            currency,
            notation: compact ? 'compact' : 'standard',
            minimumFractionDigits: compact ? 0 : decimals,
            maximumFractionDigits: compact ? 1 : decimals
        }).format(amount);
    }
    catch (e) {
        // Unknown currency code: show the number and the code as given
        return `${formatNumber(amount, { decimals })} ${currency}`;
    }
}
/**
 * @function formatNumber
 * @since 1.0.0
 */
export function formatNumber(value, options = {}) {
    const { decimals = 0, compact = false } = options;
    return new Intl.NumberFormat(appLocale(), {
        notation: compact ? 'compact' : 'standard',
        maximumFractionDigits: decimals
    }).format(Number(value) || 0);
}
/**
 * @function formatPercent
 * @since 1.0.0
 * @param value A percentage (35.68 for 35.68 %).
 */
export function formatPercent(value, decimals = 1) {
    return new Intl.NumberFormat(appLocale(), {
        style: 'percent',
        maximumFractionDigits: decimals
    }).format((Number(value) || 0) / 100);
}
/**
 * @function formatDate
 * @since 1.0.0
 * Short readable date (e.g. "16 sept. 2026"). Plain YYYY-MM-DD values are
 * read as local dates so they do not shift by a day.
 */
export function formatDate(value) {
    if (!value) {
        return '';
    }
    const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(value + 'T12:00:00')
        : new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    return new Intl.DateTimeFormat(appLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    }).format(date);
}
/**
 * @function formatRelativeDate
 * @since 1.0.0
 * "aujourd'hui", "hier", "dans 3 jours", "il y a 2 semaines"… Beyond two
 * months the short date reads better.
 */
export function formatRelativeDate(value, now = new Date()) {
    if (!value) {
        return '';
    }
    const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(value + 'T12:00:00')
        : new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOfDay(date) - startOfDay(now)) / 86400000);
    const rtf = new Intl.RelativeTimeFormat(appLocale(), {
        numeric: 'auto'
    });
    if (Math.abs(days) < 7)
        return rtf.format(days, 'day');
    if (Math.abs(days) < 60)
        return rtf.format(Math.round(days / 7), 'week');
    return formatDate(date);
}
